import log from 'loglevel';
export { default } from 'loglevel';
import { useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

const hardcodedDefaultLocale = "en";
const hardcodedDefaultSupportedLocales = [
    "en",
    "de",
    "es",
    "fa",
    "fr",
    "it",
    "ja",
    "ko",
    "pt-br",
    "ru",
    "tr",
    "zh",
    "uk",
    "pl"
];
// 2. Читаем конфигурацию из window/global, если она есть
// Поддерживаем как browser (window), так и non-browser окружения (globalThis)
const windowConfig = (typeof window !== 'undefined' ? window.FX_CONFIG : globalThis.FX_CONFIG) || {};
// Безопасное получение переменных окружения
// Vite заменяет import.meta.env.X на строки во время сборки
// Обрабатываем каждую переменную отдельно с try-catch для WeChat совместимости
let VITE_APP_BUILD_MODE;
let VITE_APP_ROUTER_BASEPATH;
let VITE_APP_API_BASE_PATH;
let BASE_URL = "/";
try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
        VITE_APP_BUILD_MODE = import.meta.env.VITE_APP_BUILD_MODE;
        VITE_APP_ROUTER_BASEPATH = import.meta.env.VITE_APP_ROUTER_BASEPATH;
        VITE_APP_API_BASE_PATH = import.meta.env.VITE_APP_API_BASE_PATH;
        BASE_URL = import.meta.env.BASE_URL ?? "/";
    }
}
catch (e) {
    // В WeChat import.meta может не работать - используем значения по умолчанию
}
// 3. Определяем routerBasePath
//    - standalone режим использует HashRouter, ему не нужен basename.
//    - В обычном режиме берем путь из window.FX_CONFIG или из переменной окружения как фолбэк.
const routerBasePath = VITE_APP_BUILD_MODE === "standalone"
    ? "/"
    : (windowConfig.routerBasePath ?? VITE_APP_ROUTER_BASEPATH ?? BASE_URL ?? "/");
// 4. Определяем apiBasePath. Он также может быть динамическим.
// Сначала берем из конфига
// Потом берем из окружения
// Потом проверяем - а может у нас ассеты по полному пути берутся? тогда надо апи оттуда же брать
// Потом уже берем с текущего пути
const baseUrl = BASE_URL;
const locationOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : "";
const apiBasePath = windowConfig.apiBasePath ??
    (VITE_APP_API_BASE_PATH != null
        ? VITE_APP_API_BASE_PATH
        : baseUrl.toLowerCase().startsWith("http")
            ? // убираем трейлинг слеш
                baseUrl.replace(/\/+$/, "")
            : // Убирается трейлинг слеш
                `${locationOrigin}${routerBasePath}`.replace(/\/+$/, ""));
const routerType = windowConfig.routerType == null
    ? VITE_APP_BUILD_MODE === "standalone"
        ? "HashRouter"
        : "BrowserRouter"
    : windowConfig.routerType == "HashRouter"
        ? "HashRouter"
        : "BrowserRouter";
const normalizedBaseUrl = routerBasePath !== "/" && routerBasePath !== "./" ? routerBasePath : "";
const game = windowConfig.game;
const forcedAdapter = windowConfig.forcedAdapter;
const supportedLocales = windowConfig.supportedLocales ?? hardcodedDefaultSupportedLocales;
const defaultLocale = windowConfig.defaultLocale ?? hardcodedDefaultLocale;
// 5. Экспортируем единый объект конфигурации
const AppConfig = {
    game,
    forcedAdapter,
    routerBasePath,
    apiBasePath,
    routerType,
    normalizedBaseUrl,
    defaultLocale,
    supportedLocales
};
// export const routerBasePath =
// 	import.meta.env.VITE_APP_BUILD_MODE !== "standalone"
// 		? (import.meta.env.VITE_APP_ROUTER_BASEPATH ?? import.meta.env.BASE_URL)
// 		: undefined;
//
//
// // export const apiBasePath = import.meta.env.VITE_APP_API_BASE_PATH
// // 	? import.meta.env.VITE_APP_API_BASE_PATH
// // 	: `${window.location.origin}${normalizedBaseUrl}`;
//
// export const apiBasePath =
// 	import.meta.env.VITE_APP_API_BASE_PATH != null
// 		? import.meta.env.VITE_APP_API_BASE_PATH
// 		: normalizedBaseUrl.startsWith("http")
// 			? normalizedBaseUrl
// 			: `${window.location.origin}${normalizedBaseUrl}`;

// src/lib/logger.ts
// В WeChat mini game window.location может не существовать
const queryParams = typeof window !== 'undefined' && window.location
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams('');
const hasRemotejs = queryParams.has("remotejs");
// Проверяем, не был ли уже инициализирован наш кастомный логгер
// Это защита от двойной инициализации в случае дублирования модуля
const FXID_LOGGER_MARKER = '__fxid_custom_logger__';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
if (!log[FXID_LOGGER_MARKER]) {
    // Помечаем логгер как инициализированный
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    log[FXID_LOGGER_MARKER] = true;
    // Сохраняем оригинальную фабрику методов для последующего вызова
    const originalFactory = log.methodFactory;
    // Определяем стилизованный префикс и его стили
    const styledPrefix = "%c FXID ";
    const styledPrefixCss = "background: #000; color: #fff; padding: 2px 4px; border-radius: 3px;";
    // Стиль для сброса форматирования для остальной части сообщения
    const resetCss = "";
    // Переопределяем фабрику методов, чтобы добавить префикс и обработать remotejs
    log.methodFactory = (methodName, level, loggerName) => {
        const rawMethod = originalFactory(methodName, level, loggerName);
        return (...args) => {
            // Проверяем, является ли первый аргумент строкой для форматирования
            const isFirstArgFormatString = typeof args[0] === "string";
            let newArgs;
            if (isFirstArgFormatString) {
                const userFormatString = args[0];
                const remainingArgs = args.slice(1);
                // --- Объяснение трюка ---
                // Чтобы стилизовать только префикс, мы делаем следующее:
                // 1. Формируем новую строку формата: `НАШ_СТИЛЬ НАШ_ТЕКСТ СБРОС_СТИЛЯ ПОЛЬЗОВАТЕЛЬСКИЙ_ТЕКСТ`
                //    Пример: `%c FXID %crouterBasePath: %s`
                // 2. Передаем аргументы для стилей: сначала для нашего префикса, потом пустой для сброса.
                //    Пример: `['стиль_для_fxid', '', '/']`
                // В итоге console.log правильно отформатирует только префикс.
                newArgs = [
                    `${styledPrefix}%c${userFormatString}`, // -> "%c FXID %crouterBasePath: %s"
                    styledPrefixCss,
                    resetCss,
                    ...remainingArgs
                ];
            }
            else {
                // Если первый аргумент не строка (например, log.debug({ object })),
                // то просто выводим стилизованный префикс, а за ним остальные аргументы.
                // В этом случае сброс не нужен, т.к. префикс и так является отдельным аргументом.
                newArgs = [styledPrefix, styledPrefixCss, ...args];
            }
            // Вызываем оригинальный метод с новыми, модифицированными аргументами
            rawMethod(...newArgs);
            // Дублируем вывод в глобальную консоль в режиме remotejs
            if (hasRemotejs) {
                // eslint-disable-next-line no-console
                const consoleMethod = console[methodName];
                if (typeof consoleMethod === "function") {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
                    consoleMethod(...newArgs);
                }
            }
        };
    };
    // Пересобираем логгер с новой фабрикой методов
    log.rebuild();
    // Делаем log доступным глобально
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    globalThis.log = log;
    // Логируем сообщение об инициализации
    log.debug(hasRemotejs ? "Remotejs enabled: FXID prefix + console duplication" : "Standard mode: FXID prefix active");
}

const AuthTokenLocalStorageKey = "AuthToken";
const DEFAULT_STATE = {
    isWebPlayLoginEnabled: false,
    isHeaderLoginEnabled: true,
    isUserProfileSettingsButtonEnabled: true,
    authToken: localStorage.getItem(AuthTokenLocalStorageKey)
};
const useAppStore = create()(persist(immer((set) => ({
    ...DEFAULT_STATE,
    enableWebPlayLogin: () => set((state) => {
        state.isWebPlayLoginEnabled = true;
    }),
    updateHeaderLogin: (newState) => set((state) => {
        state.isHeaderLoginEnabled = newState;
    }),
    updateUserProfileSettingsButton: (newState) => set((state) => {
        state.isUserProfileSettingsButtonEnabled = newState;
    }),
    setAuthToken: (newState) => {
        set((state) => {
            state.authToken = newState;
        });
        // TODO:  говно хак - так код писали обезьяны с гранатой, нужно удалить все из кеша react-query при смене токена
        // возможно надо как то более аккуратно чистить, но пока грохаю все
        log.info("Clearing react-query cache after auth token change");
        useQueryClient().clear();
    },
})), {
    name: "app-store", // unique name for localStorage key
    partialize: (state) => ({
        authToken: state.authToken
    })
}));
const useWebPlayLoginState = () => useAppStore((state) => state.isWebPlayLoginEnabled);
const useHeaderLoginState = () => {
    const update = useAppStore((state) => state.updateHeaderLogin);
    const enabled = useAppStore((state) => state.isHeaderLoginEnabled);
    const enable = () => update(true);
    const disable = () => update(false);
    const reset = () => update(DEFAULT_STATE.isHeaderLoginEnabled);
    return [enabled, { enable, disable, reset }];
};
const useUserProfileSettingsButtonState = () => {
    const update = useAppStore((state) => state.updateUserProfileSettingsButton);
    const enabled = useAppStore((state) => state.isUserProfileSettingsButtonEnabled);
    const enable = () => update(true);
    const disable = () => update(false);
    const reset = () => update(DEFAULT_STATE.isUserProfileSettingsButtonEnabled);
    return [enabled, { enable, disable, reset }];
};
const appStore = {
    getState: useAppStore.getState,
    setState: useAppStore.setState,
    subscribe: useAppStore.subscribe
};

// Ссылка на безопасное хранилище Яндекса. Изначально null.
let yandexSafeStorage = null;
let currentAuthToken = null;
/**
 * Регистрирует SafeStorage от Яндекс.Игр.
 * Вызывается из адаптера после инициализации YSDK.
 * @param storage - SafeStorage объект из Yandex SDK
 */
function registerYandexStorage(storage) {
    yandexSafeStorage = storage;
    log.info("Yandex SafeStorage registered in Auth module");
    // Попытка восстановить сессию, если localStorage был очищен (проблема iOS)
    try {
        const yaToken = storage.getItem(AuthTokenLocalStorageKey);
        const localToken = localStorage.getItem(AuthTokenLocalStorageKey);
        if (yaToken && !localToken) {
            log.info("Restoring auth token from Yandex SafeStorage to LocalStorage");
            setAuthToken(yaToken);
        }
        else if (currentAuthToken && !yaToken) {
            // Если у нас есть токен в памяти/local, но нет в яндексе - синхронизируем в яндекс
            try {
                storage.setItem(AuthTokenLocalStorageKey, currentAuthToken);
            }
            catch (e) {
                log.error("Failed to sync token to Yandex storage", e);
            }
        }
    }
    catch (err) {
        log.error("Error during Yandex storage initialization", err);
    }
}
function resetAuthToken() {
    currentAuthToken = null;
    // Очищаем localStorage
    try {
        localStorage.removeItem(AuthTokenLocalStorageKey);
    }
    catch (err) {
        log.error("Failed to clear localStorage", err);
    }
    // Очищаем Yandex storage
    if (yandexSafeStorage) {
        try {
            yandexSafeStorage.removeItem(AuthTokenLocalStorageKey);
        }
        catch (err) {
            log.error("Failed to clear Yandex storage", err);
        }
    }
    // Update appStore
    appStore.getState().setAuthToken(null);
}
function setAuthToken(token) {
    currentAuthToken = token;
    // 1. Пишем в LocalStorage (стандартное поведение)
    try {
        localStorage.setItem(AuthTokenLocalStorageKey, token);
    }
    catch (err) {
        log.error("Failed to write to localStorage", err);
    }
    // 2. Дублируем в Яндекс (если доступен)
    if (yandexSafeStorage) {
        try {
            yandexSafeStorage.setItem(AuthTokenLocalStorageKey, token);
        }
        catch (err) {
            log.error("Failed to write to Yandex storage", err);
        }
    }
    // 3. Update appStore
    appStore.setState((state) => {
        state.authToken = token;
    });
}
function getAuthToken() {
    let token = null;
    // 1. Пробуем читать из LocalStorage
    try {
        token = localStorage.getItem(AuthTokenLocalStorageKey);
    }
    catch (err) {
        log.error("Failed to read from localStorage", err);
    }
    // 2. Если не нашли и есть Яндекс хранилище - пробуем оттуда
    if (!token && yandexSafeStorage) {
        try {
            token = yandexSafeStorage.getItem(AuthTokenLocalStorageKey);
            // Если нашли в Яндексе, но не было в LocalStorage - синхронизируем
            if (token) {
                currentAuthToken = token;
            }
        }
        catch (err) {
            log.error("Failed to read from Yandex storage", err);
        }
    }
    // 3. Фолбек на переменную в памяти
    return token || currentAuthToken;
}
function authTokenExists() {
    try {
        // Проверка в LocalStorage
        if (localStorage.getItem(AuthTokenLocalStorageKey) != null)
            return true;
        // Проверка в Yandex Storage
        if (yandexSafeStorage && yandexSafeStorage.getItem(AuthTokenLocalStorageKey) != null)
            return true;
    }
    catch (err) {
        log.error("Failed to check auth token existence", err);
    }
    return currentAuthToken != null;
}

/**
 * Получает base origin для URL
 * В browser окружении использует window.location.origin
 * В non-browser окружении (WeChat) использует AppConfig.apiBasePath
 */
function getBaseOrigin() {
    if (typeof window !== 'undefined' && window.location) {
        log.debug("getBaseOrigin: using window.location.origin", window.location.origin);
        return window.location.origin;
    }
    // В WeChat или других non-browser окружениях используем apiBasePath
    log.debug("getBaseOrigin: using AppConfig.apiBasePath", AppConfig.apiBasePath);
    return AppConfig.apiBasePath;
}
/**
 * https://stackoverflow.com/a/58362609
 */
const exitFullscreen = async () => {
    try {
        // As correctly mentioned in the accepted answer, exitFullscreen only works on document
        const cancellFullScreen = document.exitFullscreen ||
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            document.mozCancelFullScreen ||
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            document.webkitExitFullscreen ||
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            document.msExitFullscreen;
        log.info("Exiting fullscreen");
        await cancellFullScreen.call(document);
        log.info("Exited fullscreen");
    }
    catch (err) {
        // Тут будет ошибка дескать "мы не вышли из фулскрина потому-что мы не там - нам не важно
        // ignore
    }
};
function errorToString(error) {
    if (!error) {
        return "Unknown error";
    }
    // If it's an Error instance
    if (error instanceof Error) {
        const errorMessage = error.message;
        // Handle case where error.message is an object
        if (typeof errorMessage === "object" && errorMessage !== null) {
            try {
                return JSON.stringify(errorMessage);
            }
            catch {
                // ignore
            }
        }
        // Handle string messages and add stack trace in development
        return process.env.NODE_ENV === "development" ? `${errorMessage}\n${error.stack || ""}` : errorMessage;
    }
    // Handle non-Error objects
    if (typeof error === "object") {
        try {
            return JSON.stringify(error);
        }
        catch {
            // ignore
        }
    }
    // Handle primitive values
    return String(error);
}
/**
 * Проверяет, является ли переданная строка абсолютным URL-адресом.
 */
const isAbsoluteURL = (url) => {
    try {
        new URL(url);
        return true;
    }
    catch (_e) {
        return false;
    }
};
/**
 * Безопасно объединяет базовый URL (или путь) с дополнительным путем.
 * @param base - Базовый URL или путь
 * @param path - Дополнительный путь для объединения
 * @returns Новая строка с объединенным URL/путем
 */
const joinURL = (base, path) => {
    if (isAbsoluteURL(base)) {
        return new URL(path, base).toString();
    }
    const cleanedBase = base.replace(/\/$/, "");
    const cleanedPath = path.replace(/^\//, "");
    const result = `${cleanedBase}/${cleanedPath}`;
    if (result.startsWith("//")) {
        return result.substring(1);
    }
    return result;
};
/**
 * Находит конфигурацию адаптера по типу
 * @param adapters - Массив адаптеров
 * @param adapter - Тип адаптера для поиска
 * @returns Конфигурация адаптера или undefined
 */
function findAdapterConfig(adapters, adapter) {
    if (!adapter || !adapters) {
        return undefined;
    }
    return adapters.find((el) => el.IframeType === adapter);
}
/**
 * Карта существующих адаптеров с документацией
 */
const EXISTING_ADAPTERS_MAP = new Map([
    ["fxid", true],
    ["ok", true],
    ["yandex", true]
]);
/**
 * Получает URL документа для указанного адаптера
 * @param params - Параметры: docFile и adapter
 * @param baseUrl - Базовый URL приложения
 * @param language - Язык документа (ru или en)
 * @returns URL документа
 */
function getDocUrl(params, baseUrl, language) {
    const adapterParam = params.adapter ?? "fxid";
    const isAbsoluteUrl = baseUrl.startsWith('http');
    const origin = getBaseOrigin();
    log.debug("getDocUrl: origin =", origin, "baseUrl =", baseUrl, "isAbsoluteUrl =", isAbsoluteUrl);
    const docsPath = isAbsoluteUrl
        ? `${baseUrl}/docs`
        : `${origin}${baseUrl}/docs`;
    const lang = language === "ru" ? "ru" : "en";
    const adapter = EXISTING_ADAPTERS_MAP.get(adapterParam) ? adapterParam : "fxid";
    const fullPath = `${docsPath}/${adapter}/${lang}/${params.docFile}`;
    log.debug("getDocUrl: constructing URL with fullPath =", fullPath);
    return new URL(fullPath);
}

export { AppConfig, AuthTokenLocalStorageKey, appStore, authTokenExists, errorToString, exitFullscreen, findAdapterConfig, getAuthToken, getBaseOrigin, getDocUrl, joinURL, registerYandexStorage, resetAuthToken, setAuthToken, useAppStore, useHeaderLoginState, useUserProfileSettingsButtonState, useWebPlayLoginState };
//# sourceMappingURL=index.js.map
